// Town of Brookline 2030 Roadmap
// Version 1
// June 3, 2026

export const roadmapData = {
  title: "Town of Brookline 2030 Roadmap",
  updatedDate: "June 9, 2026",
  purposeAndProcess: [
    "The 2030 Roadmap captures the shared strategic priority areas and goals of Select Board members and the Select Board Office. It will guide the Select Board, Select Board Office, and Town Departments in their work, giving staff firm ground to stand on and confidence to pursue their work through changing environments and elected official turnover.",
    "The 2030 Roadmap was iteratively developed between July 2025 and May 2026. The process began with information gathering through individual interviews with Select Board members and department heads, and discussion with the Select Board Office. A summary of interview findings was shared with all who participated. The development of the 2030 Roadmap was discussed at four Select Board workshops (July 29, 2025, October 14, 2025, December 9, 2025, and March 17, 2026), and an update was provided at the February 10, 2026, Select Board meeting. The Select Board, Select Board Office, and department heads reviewed draft versions of the 2030 Roadmap to offer feedback multiple times during its development. It was presented at the May 12, 2026, Select Board Meeting and adopted at the May 19, 2026, Select Board meeting.",
  ],
  goalsAndActions: [
    "The Select Board has identified seven goals for inclusion in the 2030 Roadmap. Each goal includes framing, strategies, and key performance indicators. Each strategy includes actions to advance strategies towards the overarching goal. Actions are separated by Select Board and Town staff. The list of actions taken to advance the strategies will likely change over the five-year timeline; new actions will be identified and existing actions will be tried and found unproductive or irrelevant.",
  ],
  guidingValues: {
    intro: "The Town of Brookline Select Board identified the following guiding values that inform their priorities for the Town and should be considered when implementing the 2030 Roadmap.",
    brookline: [
      "Being a welcoming, accessible, accepting, and diverse community.",
      "Being a predictable and stable place for residents, visitors, and businesses.",
    ],
    government: [
      "Continuous improvement of efficiency and outcomes.",
      "Transparency and responsiveness.",
      `Decision-making that incorporates feedback from affected stakeholders and the broader community; is based on the best information available, including science, data, input from experts; and is conducted in a fiscally responsible manner.`,
      "Good governance and collaboration across departments and public bodies, i.e., Boards, Commissions, and Advisory Bodies.",
      "Commitment to the values of diversity, equity, and inclusion for all and excellence in its service provision to all residents.",
      "A growing economy.",
    ],
  },
  goals: [
    {
      id: 1,
      title: "Deliver core services to residents reliably and efficiently within the limits of available resources.",
      framing: `The Town of Brookline provides high-quality services to its residents such as public education, public safety, waste pick-up, libraries, recreation, public health and human services, permitting, and road maintenance. The Select Board wants the Town to provide these services within its organizational capacity and resources so that services are high-quality and sustainable. Most of the Town’s time and resources are spent delivering these core services, some of which are required by law and others promote our values as a town.`,
      kpis: [
        { indicator: "Departments set service goals", target: "All departments set goal by FY27" },
        { indicator: "Dashboard developed to track service provision", target: "Dashboard developed by FY29" },
        { indicator: "Reporting dashboard published", target: "Annually" },
      ],
      strategies: [
        {
          id: "A",
          title: "Document and communicate to residents the strength of Town services and improve transparency on service performance.",
          actions: [
            {
              id: "1.A.1",
              departments: "Legal interns or SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Document the required base services each department provides, levels of service, enforcement requirements, and the source of the requirement (e.g., federal regulation, state statute, warrant article, public body, public demand)",
            },
            {
              id: "1.A.2",
              departments: "SBO and Comms",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop a dashboard using already-captured data that tracks and illustrates service provision in the Town. Highlight the dashboard in regular outbound communications, including social media, press and direct communications.",
            },
            {
              id: "1.A.3",
              departments: "SBO and Comms",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Determine the appropriate way to assess resident satisfaction with core services. Consider combining surveying efforts when possible and using existing Town survey platforms.",
            },
          ],
        },
        {
          id: "B",
          title: "Promote the continuous improvement, efficiency, and cost awareness of services.",
          actions: [
            {
              id: "1.B.1",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Annual",
              owner: "Select Board",
              description: "Align staff resources (both additional staff and competitive compensation for certain roles) and physical buildings and space (investment in new and existing buildings or parking) in upcoming budget cycles.",
            },
            {
              id: "1.B.2",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Expressly include resources needed to implement warrant articles in Select Board reports.",
            },
            {
              id: "1.B.3",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Review and evaluate staff recommendations through departmental organizational reviews as they progress. (See also <a href=\"/roadmap-2030#3\">Goal 3</a>.)",
            },
            {
              id: "1.B.4",
              departments: "SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop recommendations through departmental organizational reviews as they progress. (See also <a href=\"/roadmap-2030#3\">Goal 3</a>.)",
            },
          ],
        },
      ],
    },
    {
      id: 2,
      title: "Strengthen the alignment of goals and practices, improve efficiency, and strengthen governance of Town government.",
      framing: `Brookline’s complex system of departments and public bodies requires clear alignment, modernized systems, and predictable processes. Improved coordination enhances accountability, reduces duplication, strengthens policy implementation, and ensures consistent experiences for both residents and staff.`,
      kpis: [
        { indicator: "Warrant Articles requiring major amendments, referrals or negotiation due to misalignment or process gaps", target: "50% reduction by 2030" },
        { indicator: "% of Select Board annual policy goals achieved", target: "100% by 2030" },
        { indicator: "Select Board legislative agenda adopted", target: "Annually" },
      ],
      strategies: [
        {
          id: "A",
          title: "Modernize, coordinate, and streamline operations.",
          actions: [
            {
              id: "2.A.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Review and adopt the updated Comprehensive Plan.",
            },
            {
              id: "2.A.2",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Prioritize efficiencies through information technology to improve service delivery, lower costs, and realize measurable productivity gains. Potential efforts could include funding trainings and testing of new IT systems and tools; fund increased migration to the cloud; explore use of business analyst to assess departmental utilization of technology; prioritize the continued digitization of records, especially in the Town Clerk, Buildings, Human Resources, and PHHS departments.",
            },
            {
              id: "2.A.3",
              departments: "SBO/Comms",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Promote existing systems that improve response to citizen inquiries.",
            },
            {
              id: "2.A.4",
              departments: "SBO, and all depts",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1-Ongoing",
              owner: "Town Staff",
              description: `With the Select Board and Town Administrator’s Office, review prioritized recommendations and propose a multi-year funding plan to implement the Comprehensive Plan, and track progress of those goals and recommendations online.`,
            },
            {
              id: "2.A.5",
              departments: "SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Evaluate and implement standard operating procedures and other job aids where appropriate to improve operational efficiency and consistency.",
            },
            {
              id: "2.A.6",
              departments: "SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Share resources between departments for common functions, such as writing and managing grants, and business services.",
            },
            {
              id: "2.A.7",
              departments: "IT",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: "Establish an internal staff committee/user group to streamline online processes (e.g., permits, licenses, Medicaid reimbursements, human resources processes) and reduce manual workflows and track the time each process takes.",
            },
          ],
        },
        {
          id: "B",
          title: "Work with the School Committee to improve the function of the Town School Partnership.",
          actions: [
            {
              id: "2.B.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Determine whether the Expenditures & Revenues Study Committee will continue beyond the spring and, if so, its role in relation to a new role and function of the Town School Partnership.",
            },
            {
              id: "2.B.2",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "With the Town Administrator, develop a revised <a href=\"/town-school-moa\">Memorandum of Agreement</a> between the Town Administrator and Superintendent.",
            },
            {
              id: "2.B.3",
              departments: "SBO",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: `With the Superintendent, develop a revised Town School Partnership <a href="/town-school-moa">Memorandum of Agreement</a>. Base this on the Select Board’s decision regarding Expenditures & Revenues Study Committee and the Town School Partnership.`,
            },
          ],
        },
        {
          id: "C",
          title: "Improve coordination on and clarity about when to advance major planning projects.",
          actions: [
            {
              id: "2.C.1",
              departments: "SBO",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Increase predictability of study and planning schedule by developing a replacement schedule for regular studies and master plans (e.g., pension review, Housing Production Plan, Parks & Open Space, Community Health Improvement Plan) to ensure their continuing relevance under changing conditions and priorities, and increase compliance.",
            },
            {
              id: "2.C.2",
              departments: "SBO",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Annual",
              owner: "Town Staff",
              description: "Inventory and update large planning processes in progress in the Town including the strategic direction to undertake them.",
            },
          ],
        },
        {
          id: "D",
          title: "Increase efficacy and efficiency of public bodies.",
          actions: [
            {
              id: "2.D.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "With staff recommendation, consolidate or eliminate public bodies that no longer serve a core municipal purpose or are dormant.",
            },
            {
              id: "2.D.2",
              departments: "SBO",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Develop an evaluation protocol to and routinely assess efficacy of public bodies and propose improvements.",
            },
            {
              id: "2.D.3",
              departments: "SBO",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Define roles and expectations for Select Board liaisons, public body members, staff, including when staff and Select Board involvement is necessary.",
            },
            {
              id: "2.D.4",
              departments: "SBO and all depts",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Reduce the administrative burden for staff while remaining in compliance with Open Meeting Law, e.g., explore technology use for transcriptions and summaries, and develop standardized templates.",
            },
          ],
        },
        {
          id: "E",
          title: "Improve warrant article development and review, and enforcement of by-laws.",
          actions: [
            {
              id: "2.E.1",
              departments: "",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Select Board",
              description: "During warrant articles, confirm that they have been properly vetted with staff in advance of filing, and ensure that they are in line with previously stated priorities and work plans. If a warrant article causes the Select Board to reprioritize, identify the impact on redirecting staff time.",
            },
            {
              id: "2.E.2",
              departments: "SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Work with Moderator & Advisory Committee to develop standards for, and processes to educate potential petitioners about, warrant articles, e.g., identifying funding sources for policies with funding implications.",
            },
          ],
        },
        {
          id: "F",
          title: "Develop a legislative agenda for Town Meeting.",
          actions: [
            {
              id: "2.F.1",
              departments: "",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Select Board",
              description: `Discuss the Town legislative agenda at the Select Board’s summer or early fall retreat.`,
            },
            {
              id: "2.F.2",
              departments: "",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Select Board",
              description: `Engage with state elected and appointed officials by joining with the School Committee’s annual meeting with state legislators.`,
            },
            {
              id: "2.F.3",
              departments: "",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Select Board",
              description: "With assistance of the Town Administrator, reach out and become more visible to Mayors and City Council members in Boston and Newton, and be aware of common problems and concerns.",
            },
          ],
        },
      ],
    },
    {
      id: 3,
      title: `Increase the Town’s fiscal health and stability.`,
      framing: `Brookline faces a structural deficit driven by rising fixed costs, limited revenue growth under Proposition 2-1/2, aging infrastructure, reduced federal and state funds, and growing service demands. A sustainable financial future requires disciplined prioritization, revenue diversification, proactive economic development, and modern financial policies and practices that protect the Town’s bond rating and debt service capacity.`,
      kpis: [
        { indicator: "Bond rating", target: "AAA bond rating is maintained" },
        { indicator: "New growth revenue", target: "$15M-$20M new growth revenue is obtained between FY26 and FY30" },
        { indicator: "Other new revenue growth", target: "Public/private site control and conceptual planning completed for a combination of projects that would likely see $15 M of new growth upon construction completion" },
        { indicator: "5-year service and funding plan adoption", target: "5-year service and funding prioritization plan is adopted by FY27" },
      ],
      strategies: [
        {
          id: "A",
          title: "Ensure the recommendations of the Expenditures & Revenues Study Committee are responded to and implemented as appropriate.",
          actions: [
            {
              id: "3.A.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Review and assess the work of the Expenditures & Revenues Study Committee to determine whether it should remain as a standing committee of the Town and its relationship to the Town School Partnership.",
            },
            {
              id: "3.A.2",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: `Determine whether the Expenditures & Revenues Study Committee should be used to ensure the continuation of efforts to address fiscal constraints and solutions to fiscal challenges in a Proposition 2-½ environment.`,
            },
            {
              id: "3.A.3",
              departments: "Planning",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Establish a subcommittee of the Economic Development Advisory Board (EDAB), which may include an on-call real estate consultant, that could meet three times a year with the Select Board to review all evolving economic development opportunities as well as potential major parcels that may be developed or coming to the market. These discussions may require executive session, if related to potential real estate negotiating position.",
            },
          ],
        },
        {
          id: "B",
          title: "Increase revenue through development.",
          actions: [
            {
              id: "3.B.1",
              departments: "Planning",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Proactively seek out mixed-use development opportunities, including commercial and residential.",
            },
            {
              id: "3.B.2",
              departments: "Planning, Building, SBO",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Make development easier through streamlined permitting process and more effective use of public engagement.",
            },
            {
              id: "3.B.3",
              departments: "Planning",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: `Staff and EDAB will annually present to the Select Board’s public meeting their updated five-year economic development plan (including specific strategies for new revenue growth from commercial properties).`,
            },
          ],
        },
        {
          id: "C",
          title: "Increase revenue from non-development sources.",
          actions: [
            {
              id: "3.C.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Work with legislative delegation to petition the Legislature to approve additional, local option taxes.",
            },
            {
              id: "3.C.2",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Work with the Massachusetts Municipal Association on their plans to petition the Legislature to increase funding to municipalities.",
            },
            {
              id: "3.C.3",
              departments: "Legal",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Assess the benefits of participation in class action municipal lawsuits versus filing individual actions to redress an injury to the Town.",
            },
            {
              id: "3.C.4",
              departments: "All depts",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Work with the Massachusetts Municipal Association on their plans to petition the Legislature to increase funding to municipalities.",
            },
            {
              id: "3.C.5",
              departments: "SBO and Finance",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Identify appropriate opportunities to increase fees and fines.",
            },
            {
              id: "3.C.6",
              departments: "Finance",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: `Review the Town’s Payment in Lieu of Taxes use agreements with local nonprofits with a focus on facility and amenity access.`,
            },
            {
              id: "3.C.7",
              departments: "Assessor",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Identify personal property that should be but is not taxed.",
            },
          ],
        },
        {
          id: "D",
          title: "Fund investments.",
          actions: [
            {
              id: "3.D.1",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Select Board",
              description: "Continue to fund reserves and unrestricted funds.",
            },
            {
              id: "3.D.2",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Develop a 5-year service and funding priority list of strategies in existing plans to receive funding when available. (See also <a href=\"/roadmap-2030#3.E\">Goal 3, Strategy E</a>.)",
            },
            {
              id: "3.D.3",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Revise the Unfunded Liabilities Policy to set deadlines for achieving full funding of the other postemployment benefit (OPEB) liabilities. Specify in the Unfunded Liabilities Policy the amount over the actuarially determined contribution to the OPEB liability that the Town will pay each year and specify clear but limited circumstance for not funding such additional amount.",
            },
            {
              id: "3.D.4",
              departments: "Finance",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: `Conduct a review of rating criteria used by rating agencies to assign a triple-A rating to municipal bonds and assess the Town’s financial policies and practices with respect to those criteria.`,
            },
          ],
        },
        {
          id: "E",
          title: "Reduce costs.",
          actions: [
            {
              id: "3.E.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Develop a 5-year service and funding priority list of strategies in existing plans to receive funding when available. (See also <a href=\"/roadmap-2030#3.D\">Goal 3, Strategy D</a>.)",
            },
            {
              id: "3.E.2",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Review and assess staff recommendations through departmental organizational review. (See also <a href=\"/roadmap-2030#1\">Goal 1</a>.)",
            },
            {
              id: "3.E.3",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Focus on hiring that will reduce costs or increase revenues more than the cost of the hire, e.g., municipal hearing officer.",
            },
            {
              id: "3.E.4",
              departments: "SBO and all depts",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop recommendations to reduce costs through departmental organizational review. (See also <a href=\"/roadmap-2030#1\">Goal 1</a>.)",
            },
            {
              id: "3.E.5",
              departments: "SBO and all depts",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Identify cost savings from actions that modernize or streamline operations. (See also <a href=\"/roadmap-2030#2\">Goal 2</a>.)",
            },
            {
              id: "3.E.6",
              departments: "SBO, PSPO",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Continue analyzing overtime usage to ensure its appropriate and identify any discretionary usage.",
            },
          ],
        },
        {
          id: "F",
          title: "Advance fiscal accountability.",
          actions: [
            {
              id: "3.F.1",
              departments: "Comms and SBO",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: `Regularly publicize information and engage residents about the nature of the Town’s financial challenges. (See also <a href=\"/roadmap-2030#4\">Goal 4</a>.)`,
            },
            {
              id: "3.F.2",
              departments: "Finance and SBO",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: "Develop and share guidance for fiscal accountability and identifying funding sources for policies.",
            },
          ],
        },
      ],
    },
    {
      id: 4,
      title: "Improve communication to and engagement with residents.",
      framing: `Residents expect timely responses, clear information, and meaningful opportunities to participate in decisions. Brookline’s diverse population requires accessible communication channels and consistent engagement practices across departments. Strengthening communication and expanding participation builds trust and ensures that policy reflects community needs. Many Town residents are actively engaged in Town government. The Select Board wants this to continue and to increase involvement from residents not historically engaged.`,
      kpis: [
        { indicator: "Select Board communication protocol reviewed annually", target: "Reviewed annually" },
        { indicator: "Responsiveness of Select Board to residents", target: "SB response expectations met 90% of the time by FY27" },
        { indicator: "Engagement standards across departments", target: "Engagement standards adopted by FY29; implemented by FY30" },
        { indicator: "Participation of underrepresented groups", target: "Participation by underrepresented groups increases annually" },
      ],
      strategies: [
        {
          id: "A",
          title: "Build on improved communication, engagement, and navigability of Town government and services for residents.",
          actions: [
            {
              id: "4.A.1",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Select Board",
              description: "Attend Town-provided emergency training on regular basis.",
            },
            {
              id: "4.A.2",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Improve communications practices at the department level, e.g., trainings, staff time, peer-to-peer sharing of best practices.",
            },
            {
              id: "4.A.3",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: `Improve the Select Board’s ability to respond to residents, e.g., revise the Select Board Communications Protocol (draft 2/5/24) and follow it, host Select Board listening sessions or office hours “on the road” at libraries or schools.`,
            },
            {
              id: "4.A.4",
              departments: "",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Select Board",
              description: "Work with staff to establish streamlined public process protocols with defined end dates for specific issues, e.g., budget proposal, zoning.",
            },
            {
              id: "4.A.5",
              departments: "All depts",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Support emergency management team in their work to provide a comprehensive and well managed standard approach to emergencies by regularly reviewing standard operating procedures and holding drills and after-action reviews as necessary.",
            },
            {
              id: "4.A.6",
              departments: "Comms",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Document and communicate to residents the strength of Town services and improve transparency on service performance. (See also <a href=\"/roadmap-2030#1\">Goal 1</a>.)",
            },
            {
              id: "4.A.7",
              departments: "Comms",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: `Regularly publicize information and engage residents about the nature of the Town’s financial challenges. (See also <a href=\"/roadmap-2030#3\">Goal 3</a>.)`,
            },
            {
              id: "4.A.8",
              departments: "SBO, CoA, ODEICR, Fire/EM, Police, PHHS",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Increase collaboration among departments providing services for vulnerable residents, e.g., mental health services and food security.",
            },
            {
              id: "4.A.9",
              departments: "DPW and Comms",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Town Staff",
              description: "Increase consistency of public engagement on public way projects and provide information about how roads are prioritized.",
            },
            {
              id: "4.A.10",
              departments: "ODEICR and Comms",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop standards for engagement and information dissemination across departments.",
            },
            {
              id: "4.A.11",
              departments: "ODEICR and Comms",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: `Incorporate user experience best practices when providing resources and information to residents, e.g., create a “one-stop” page on the website.`,
            },
            {
              id: "4.A.12",
              departments: "SBO",
              timing: ["Y5"],
              timingRaw: "Y5",
              owner: "Town Staff",
              description: "Work with Select Board to establish public process protocols depending on the issue, e.g., budget proposal, zoning.",
            },
          ],
        },
        {
          id: "B",
          title: "Diversify residents engaged in Town processes.",
          actions: [
            {
              id: "4.B.1",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Select Board",
              description: "Help recruit underrepresented voices on public bodies.",
            },
            {
              id: "4.B.2",
              departments: "Comms, PHHS, ODEICR",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Strengthen partnerships with community-based organizations to share information.",
            },
            {
              id: "4.B.3",
              departments: "SBO",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Increase multilingual support for Town meetings and resources.",
            },
            {
              id: "4.B.4",
              departments: "SBO and all depts",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Broaden the pool of residents engaged and reflected in decision making.",
            },
            {
              id: "4.B.5",
              departments: "Comms",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: `Increase subscriber base for Town Administrator’s newsletter and other Town communications.`,
            },
          ],
        },
      ],
    },
    {
      id: 5,
      title: "Increase the racial and economic diversity of Brookline households.",
      framing: `Brookline, for historical reasons, has limited Black and Hispanic racial diversity relative to the racial diversity of neighboring communities. The Select Board is committed to increasing the numbers of Black and Hispanic households in Brookline, including high income Black and Hispanic households for whom Brookline’s high home values and rents would not be a barrier. At the same time, Brookline has a growing and diverse population with backgrounds from East, Southeast, and South Asia whose participation in government should be welcomed and encouraged. In addition, the Select Board is working to protect existing middle-income housing stock and expand middle-income and low-income housing opportunities, which will enable the Town to attract and maintain racial and other diversity. Preserving existing affordability and creating opportunities for households of all income levels is essential for economic diversity, civic stability, and a sustainable local economy. Brookline should have reasonably priced housing for the breadth of residents we want to live in our Town. This will require revisiting whether our zoning and regulatory environment is helping the Town achieve these goals.`,
      kpis: [
        { indicator: "Marketing policy development", target: "Created by 2030" },
        { indicator: "Variance in vacancy rate of rental housing", target: "By 2030, noticeable increase and similar or lower than adjacent neighborhoods in other communities. Long-term Target: 5%" },
        { indicator: "Availability of affordable housing units", target: "By 2033, 9.2% of year-round housing units are affordable units for households at or below 120% Area Median Income" },
        { indicator: "Black and Hispanic households", target: "Increase in Black and Hispanic households" },
      ],
      strategies: [
        {
          id: "A",
          title: "Proactively market Brookline to Black and Hispanic families, especially high-income families.",
          actions: [
            {
              id: "5.A.1",
              departments: "Comms, ODEICR",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop a marketing plan targeting Black and Hispanic families, especially high-income families. Share information with institutions such as churches, universities, hospitals, and other industries who regularly recruit high income individuals to the Boston area.",
            },
          ],
        },
        {
          id: "B",
          title: "Build and preserve housing suitable for households at or below 120% Area Median Income (including but not limited to deed-restricted affordable housing).",
          actions: [
            {
              id: "5.B.1",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Working with Town staff, review and adopt the Comprehensive Plan.",
            },
            {
              id: "5.B.2",
              departments: "",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Select Board",
              description: "Review and adopt the updated Housing Production Plan by November 14, 2029.",
            },
            {
              id: "5.B.3",
              departments: "Planning",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Implement and regularly report on strategies and impacts, including tracking online recommendations from the Housing Production Plan and any related recommendations from the Comprehensive Plan.",
            },
            {
              id: "5.B.4",
              departments: "Planning",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "With recommendations from the Housing Advisory Board, develop an approach and strategy to do this, including changing incentives or requirements in the Zoning By-Law.",
            },
            {
              id: "5.B.5",
              departments: "Planning",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Market Brookline deed-restricted affordable housing available to Town and Public School of Brookline employees and retirees, in addition to the general public.",
            },
            {
              id: "5.B.6",
              departments: "Planning",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: "Remove unnecessary barriers to economically viable housing development.",
            },
          ],
        },
      ],
    },
    {
      id: 6,
      title: "Maintain and improve infrastructure quality.",
      framing: `Reliable infrastructure, including roads, buildings, utilities, technology, and mobility networks, is fundamental to public safety, economic activity, and community wellbeing. Aging assets and climate pressures require coordinated planning, sustainable investment, and modern standards to maintain, improve, and expand infrastructure to serve residents.`,
      kpis: [
        { indicator: "Achievement of Vision Zero goals", target: "Zero serious injuries and fatalities on our streets" },
        { indicator: "Road maintenance schedule created", target: "Publish and meet annual schedule for road maintenance." },
        { indicator: "Road maintenance quality", target: "Maintained PCI baseline from 2025" },
      ],
      strategies: [
        {
          id: "A",
          title: "Advance infrastructure work.",
          actions: [
            {
              id: "6.A.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Establish and fund an accelerated target for roadway maintenance.",
            },
            {
              id: "6.A.2",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: `Review infrastructure plans and inventories – e.g., roadway maintenance, building inventory – to assess progress and decide how to best advance it.`,
            },
            {
              id: "6.A.3",
              departments: "DPW, Buildings",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Provide updates to Select Board on infrastructure plans and inventories.",
            },
          ],
        },
        {
          id: "B",
          title: "Improve roadway safety.",
          actions: [
            {
              id: "6.B.1",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Select Board",
              description: "Establish and fund an accelerated target for roadway safety improvements on the High Priority Network.",
            },
            {
              id: "6.B.2",
              departments: "",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Select Board",
              description: "Ensure competitive wages allow the Town to maintain a low staff position vacancy rate.",
            },
            {
              id: "6.B.3",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: `Review roadway safety plans – e.g., Vision Zero, traffic calming, bike access – to assess progress and decide how to best advance it.`,
            },
            {
              id: "6.B.4",
              departments: "DPW",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Town Staff",
              description: "Develop and set targets for reducing the maintenance backlog. Reference specific streets, budget commitment, and numbers of lane miles to be addressed.",
            },
          ],
        },
      ],
    },
    {
      id: 7,
      title: `Improve the Town’s ability to mitigate climate change and increase resilience of assets to climate change.`,
      framing: `Climate change poses significant risks to Brookline’s infrastructure, public health, and fiscal stability. The Select Board wants to focus on actions within its control, rather than those dependent on external progress, e.g., state and regional strategies and funding sources, technological advancements.`,
      kpis: [
        { indicator: "Amount of Greenhouse Gas Emissions from Brookline", target: "Per CARP" },
        { indicator: "CARP Adoption", target: "By FY26" },
        { indicator: "Additional KPIs established from CARP", target: "By FY26" },
        { indicator: "Maintain municipally-owned open space in Town", target: "% open Space no less than FY25 baseline" },
      ],
      strategies: [
        {
          id: "A",
          title: "Advance climate action and resilience.",
          actions: [
            {
              id: "7.A.1",
              departments: "",
              timing: ["Y1"],
              timingRaw: "Y1",
              owner: "Select Board",
              description: "Review and adopt the <a href=\"/carp-2026\">Climate Action and Resilience Plan</a> (CARP).",
            },
            {
              id: "7.A.2",
              departments: "DPW (Sust), PHHS",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Implement and track progress on the <a href=\"/carp-2026\">Climate Action and Resilience Plan</a> (CARP).",
            },
            {
              id: "7.A.3",
              departments: "Building",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Town Staff",
              description: "Develop and maintain a schedule for electrification; implement stretch energy code; review fees and fines to incentivize fossil free construction",
            },
          ],
        },
        {
          id: "B",
          title: "Advance open space and recreation.",
          actions: [
            {
              id: "7.B.1",
              departments: "",
              timing: ["Y2-3"],
              timingRaw: "Y2-3",
              owner: "Select Board",
              description: "Determine funding priorities in support of acquiring land.",
              planRef: {
                badge: "Open Space & Recreation Plan",
                text: "Funding for acquisition sits under <a href=\"/open-space#resource-protection-g1\">Resource Protection, Goal 2 — acquire or create additional open space</a>, whose actions include increasing financing of the Land Acquisition Fund.",
              },
            },
            {
              id: "7.B.2",
              departments: "DPW",
              timing: ["Ongoing"],
              timingRaw: "Ongoing",
              owner: "Town Staff",
              description: "Implement and track progress on the <a href=\"/open-space\">Open Space and Recreation Plan</a> (OSRP).",
            },
            {
              id: "7.B.3",
              departments: "DPW",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: "Determine a mechanism to acquire land, e.g., Land Acquisition Fund.",
              planRef: {
                badge: "Open Space & Recreation Plan",
                text: "The mechanisms are set out in <a href=\"/open-space#resource-protection-g1-o0\">Create and implement a process for the acquisition of prioritized private open space</a> — a parcel-standards task force, the Land Acquisition Fund, right-of-first-refusal agreements and a system to track loss of open space.",
              },
            },
          ],
        },
        {
          id: "C",
          title: "Support and advance work of the Sustainability & Natural Resources Division with coordinated participation from all departments in climate change mitigation, resilience planning, and energy transition work.",
          actions: [
            {
              id: "7.C.1",
              departments: "All depts",
              timing: ["Ongoing"],
              timingRaw: "Annual",
              owner: "Town Staff",
              description: "Provide department data that captures progress on the <a href=\"/carp-2026\">CARP</a>.",
            },
            {
              id: "7.C.2",
              departments: "DPW, DPW (Sust), Building",
              timing: ["Y4-5"],
              timingRaw: "Y4-5",
              owner: "Town Staff",
              description: "Implement the <a href=\"https://www.brooklinema.gov/4061/Municipal-Fleet-Electrification\" target=\"_blank\" rel=\"noopener noreferrer\">Fleet Management, Electrification, and Vehicle Replacement Policy<span class=\"visually-hidden\"> (opens in new tab)</span></a>",
            },
          ],
        },
        {
          id: "D",
          title: "Increase the climate preparedness of Town infrastructure.",
          actions: [
            {
              id: "7.D.1",
              departments: "",
              timing: ["Y1", "Ongoing"],
              timingRaw: "Y1 and Ongoing",
              owner: "Select Board",
              description: "Provide guidance to ZEAB to advocate to electric utility companies, state regulators, and local state representatives to advance policies and actions to improve the resilience of the electric grid to be able to accommodate increased load from electrification.",
            },
          ],
        },
      ],
    },
  ],
};

export default roadmapData;
